import { Box, Popover, Typography, Divider, List, ListItem, ListItemText } from '@mui/material';
import { Button } from '../../../atomic/button';
import { CellContext } from '@tanstack/react-table';
import { EmployeeListItem } from '@pxtra-client-lib-legacy/gen';
import { useTranslation } from 'react-i18next';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { useState, useRef, useEffect } from 'react';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import { useEmployeeBenefitBudgetAndSettings } from '../../../../../queries/employee';
import Util from '../../../../../misc/util';
import { BenefitKey } from '../../../compound/dataDisplay';
import { Loading } from '../../../atomic';
import { TooltipComponent } from '../../../atomic/feedback';
import { useGetNextMonthBenefitSet } from '../../../../../queries/benefit/benefitSet';

/**
 * Cell component that displays a button showing the number of active benefits
 * When clicked, it will trigger an overlay with detailed benefits information
 */
export const BenefitsButtonCell = ({ row }: CellContext<EmployeeListItem, unknown>) => {
  const { t } = useTranslation();
  const employee = row.original;
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [showTooltip, setShowTooltip] = useState(false);
  const textRef = useRef<HTMLSpanElement>(null);
  const open = Boolean(anchorEl);
  const { data } = useEmployeeBenefitBudgetAndSettings(employee.id ?? '', {
    enabled: open
  });
  const benefitBudget = data?.benefitBudget;
  const benefitSettings = data?.settings;

  const { data: nextMonthBenefitSet, isLoading: isBenefitSetLoading } = useGetNextMonthBenefitSet(
    employee.id ?? ''
  );

  const activeBenefitKeys = benefitBudget?.active_benefits ?? [];
  const activeBenefits = Object.entries(benefitSettings ?? {}).filter(([name]) =>
    activeBenefitKeys.includes(name)
  );
  const enabledBenefits = Object.entries(benefitSettings ?? {}).filter(([name]) =>
    (nextMonthBenefitSet?.enabled_benefits ?? []).includes(name)
  );
  const grandfatheredBenefits = activeBenefits
    .filter((benefit) => !enabledBenefits.some((enabled) => enabled[0] === benefit[0]))
    .map((b) => b[0]);
  const activeBenefitsCount = activeBenefits.length;
  const benefitsList = [...activeBenefits, ...enabledBenefits]
    .filter(([name], index, self) => self.findIndex(([n]) => n === name) === index)
    .map(([name, status]) => ({
      name,
      status,
      budget: benefitBudget?.[name as BenefitKey]
    }));

  useEffect(() => {
    if (textRef.current) {
      setShowTooltip(textRef.current.scrollWidth > textRef.current.clientWidth);
    }
  }, [nextMonthBenefitSet?.name]);

  const handleBenefitsClick = (
    event: React.MouseEvent<HTMLButtonElement> | React.KeyboardEvent<HTMLButtonElement>
  ) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  if (isBenefitSetLoading) {
    return (
      <Box display="flex" alignItems="center" justifyContent="center">
        <Loading size="sm" />
      </Box>
    );
  }

  const benefitSetButton = nextMonthBenefitSet?.name ? (
    <Button
      variant="tertiary"
      trailingIcon={<KeyboardArrowDownIcon />}
      onClick={handleBenefitsClick}
      style={{ zIndex: 'auto' }}
      size="small"
    >
      <span ref={textRef} className="truncate max-w-[150px] text-base">
        {nextMonthBenefitSet.name}
      </span>
    </Button>
  ) : (
    <Button
      variant="tertiary"
      disabled
      style={{ zIndex: 'auto', whiteSpace: 'nowrap' }}
      size="small"
    >
      {t('common:NOT_ASSIGNED')}
    </Button>
  );

  return (
    <>
      {showTooltip && nextMonthBenefitSet?.name ? (
        <TooltipComponent arrow title={nextMonthBenefitSet.name}>
          <span>{benefitSetButton}</span>
        </TooltipComponent>
      ) : (
        benefitSetButton
      )}

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left'
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left'
        }}
        BackdropProps={{
          invisible: true,
          style: { backgroundColor: 'transparent', opacity: '0' }
        }}
        PaperProps={{
          className: 'w-[420px] max-h-[420px] border-xl overflow-hidden flex flex-col shadow-500'
        }}
      >
        <Box className="p-6 pb-3">
          <Typography className="text-primary-main font-bold">
            {nextMonthBenefitSet?.name ?? ''}
          </Typography>
          <Typography variant="body2" color="grey.600">
            {t('benefitSet:activeBenefitCount', {
              activeEnabledCount: activeBenefitsCount - grandfatheredBenefits.length,
              enabledCount: enabledBenefits.length
            }) +
              (grandfatheredBenefits.length > 0
                ? t('benefitSet:grandfatheredBenefitCount', {
                    grandfatheredCount: grandfatheredBenefits.length
                  })
                : '')}
          </Typography>
        </Box>

        <Divider />

        {benefitsList.length === 0 ? (
          <Box className="flex justify-center items-center p-4">
            <Loading />
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
            <Box
              sx={{
                display: 'flex',
                px: 2,
                py: 1.5
              }}
            >
              <Typography
                variant="body2"
                className="text-xs font-medium text-grey-600 pl-2"
                sx={{ flex: '2 2 45%' }}
              >
                {t('common:BENEFIT')}
              </Typography>
              <Typography
                variant="body2"
                className="text-xs font-medium text-center text-grey-600 "
                sx={{
                  flex: '1 1 10%'
                }}
              >
                {t('common:ACTIVE')}
              </Typography>
              <Typography
                variant="body2"
                className="text-xs font-medium text-center text-grey-600 "
                sx={{
                  flex: '1 1 20%'
                }}
              >
                {t('common:AMOUNT')}
              </Typography>
            </Box>
            <List
              className="flex-1 overflow-auto py-0 mx-2"
              sx={{
                '&::-webkit-scrollbar': {
                  display: 'none'
                },
                scrollbarWidth: 'none',
                msOverflowStyle: 'none',
                '& .MuiListItem-root:last-child': {
                  mb: 1
                }
              }}
            >
              {benefitsList.map(({ name, budget }, index) => (
                <ListItem
                  key={name}
                  sx={{
                    backgroundColor: index % 2 === 1 ? 'grey.50' : 'transparent',
                    height: 48,
                    px: 2,
                    display: 'flex',
                    borderRadius: '8px',
                    paddingX: '16px',
                    paddingY: '6px'
                  }}
                >
                  {grandfatheredBenefits.includes(name) ? (
                    <TooltipComponent
                      arrow
                      placement="left"
                      title={t('benefitSet:grandfatheredBenefit')}
                    >
                      <ListItemText
                        primary={t(`benefits:${name}`)}
                        sx={{ flex: '2 2 45%', margin: 0 }}
                      />
                    </TooltipComponent>
                  ) : (
                    <ListItemText
                      primary={t(`benefits:${name}`)}
                      sx={{ flex: '2 2 45%', margin: 0 }}
                    />
                  )}
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flex: '1 1 15%'
                    }}
                  >
                    {activeBenefitKeys.includes(name) ? (
                      <CheckCircleIcon sx={{ color: 'success.main' }} />
                    ) : (
                      <CancelIcon sx={{ color: 'grey.500' }} />
                    )}
                  </Box>
                  <Typography
                    sx={{
                      flex: '1 1 15%',
                      textAlign: 'right',
                      pr: 0.5
                    }}
                  >
                    {Util.formatCurrency(budget)}
                  </Typography>
                </ListItem>
              ))}
            </List>
          </Box>
        )}
      </Popover>
    </>
  );
};
